import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { captureError } from '../services/sentry';
import { useAuthContext } from '../contexts/AuthContext';

export default function useUsers() {
  const { user } = useAuthContext();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    // Try session cache first
    const cached = sessionStorage.getItem('allUsersCache');
    if (cached) {
      setAllUsers(JSON.parse(cached));
      setLoading(false);
      return;
    }

    try {
      const usersCollection = collection(db, 'users');
      const userSnapshot = await getDocs(usersCollection);
      const users = userSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      users.sort((a, b) => a.name.localeCompare(b.name));
      setAllUsers(users);
      sessionStorage.setItem('allUsersCache', JSON.stringify(users));
    } catch (error) {
      console.error("Error fetching users: ", error);
      captureError(error, { where: 'useUsers.fetchUsers' });
      alert("Could not load returning players. Check your connection or Firebase setup.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for auth before reading the users collection. Firestore rules require
  // request.auth != null; the anonymous-auth bootstrap in useAuth gives every
  // visitor a uid, but it's async, so this effect fires once `user` resolves.
  useEffect(() => {
    if (!user) return;
    fetchUsers();
  }, [user, fetchUsers]);

  const createUser = useCallback(async (name, email = '', birthday = '', authUid = null) => {
    // Firestore SDK throws if any field is `undefined`. New callers (guest →
    // profile pill, "Save as new profile" suggestion) pass only a name, so we
    // default the optional fields to empty strings rather than letting them be
    // undefined.
    const newUser = {
      name: (name || '').trim(),
      email: email || '',
      birthday: birthday || '',
      createdAt: new Date(),
    };
    if (authUid) newUser.authUid = authUid;
    const docRef = await addDoc(collection(db, "users"), newUser);
    const userWithId = { id: docRef.id, ...newUser };

    setAllUsers(prev => {
      const updated = [...prev, userWithId].sort((a, b) => a.name.localeCompare(b.name));
      sessionStorage.setItem('allUsersCache', JSON.stringify(updated));
      return updated;
    });

    return userWithId;
  }, []);

  return { allUsers, loading, createUser };
}
